<template>
  <article>
    <activity-stage :customRootContainerClass="'object-shuffle'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div
          :class="{ isMobileView: isMobile }"
          class="images-holder focusable"
          id="main-image"
          tabindex="0"
        >
          <img
            :src="currentRound?.answerImage?.image?.src"
            :alt="currentRound?.answerImage?.image?.description"
            id="main-image"
          />
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  components: { ActivityStage },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'ObjectShuffle',
      sfx: null
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(({ answerImage }) => this.queueImage(answerImage.image.src));
      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds
        .filter(({ answerImage }) => answerImage.image.src)
        .map(({ activityAudio }) => activityAudio?.audio?.src);

      this.queueAudio(soundPaths);

      const cache = await this.downloadAudio();
      return this.rounds.map(({ activityAudio }) => {
        if (!activityAudio?.audio?.src) {
          return null;
        }
        return cache.get(activityAudio?.audio?.src);
      });
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        keyboardType,
        roofs,
        showTargets
      };
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.object-shuffle', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.disableKeyboard();
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, true);

      this.handleStepEnd();
      document.getElementById('main-image')?.blur();
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['letter'];
    },

    handleStepEnd() {
      const { activityAudio } = this.currentRound;
      if (activityAudio?.audio?.src) {
        this.sfx[this.progress.roundIndex].play();
        TweenMax.delayedCall(1.75, this.finishStep);
      } else {
        TweenMax.delayedCall(0.25, this.finishStep);
      }
    },

    finishStep() {
      if (this.progress.roundIndex >= this.rounds?.length - 1) {
        this.handleRoundEnd();
      } else {
        this.setupNewRound({ stepCallback: this.stepCallback });
        this.enableKeyboard();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(0.5, this.completeActivity);
    }
  },
  watch: {
    currentRound(newVal) {
      if (newVal?.letter) {
        let instructions = `Type the ${newVal.letter} key.`;

        this.setCurrentKeyboardingSegment('');
        this.setTotalKeyboardingSegment(newVal.letter);
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.images-holder {
  height: 420px;
  text-align: center;
  z-index: 100;

  img {
    height: auto;
    max-height: 100%;
    max-width: 100%;

    width: auto;
  }
}

.chrome-os,
.images-holder {
  &.isMobileView {
    height: 325px;

    img {
      max-height: 80%;
      max-width: 80%;
    }
  }
}
</style>
